import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, IconButton, InputAdornment,
  CircularProgress, Pagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, MenuItem, Alert, LinearProgress, Divider,
} from '@mui/material';
import { Add, Edit, Delete, Search, Inventory, PhotoCamera, Upload, Download, Tune, PriceChange } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

function VariantsDialog({ product, open, onClose }) {
  const dispatch = useDispatch();
  const [variants, setVariants] = useState([{ attribute_name: 'Size', attribute_value: '', sku: '', price: '', stock: 0 }]);
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState([]);

  useEffect(() => {
    if (!open || !product) return;
    api.get(`/products/${product.id}/variants`).then(r => setExisting(r.data.data || [])).catch(() => {});
  }, [open, product]);

  const addRow = () => setVariants(v => [...v, { attribute_name: 'Size', attribute_value: '', sku: '', price: '', stock: 0 }]);
  const removeRow = (i) => setVariants(v => v.filter((_, idx) => idx !== i));
  const update = (i, field, val) => setVariants(v => v.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const save = async () => {
    setLoading(true);
    try {
      await api.post(`/products/${product.id}/variants`, { variants });
      dispatch(showSnackbar({ message: 'Variants saved', severity: 'success' }));
      const r = await api.get(`/products/${product.id}/variants`);
      setExisting(r.data.data || []);
      setVariants([{ attribute_name: 'Size', attribute_value: '', sku: '', price: '', stock: 0 }]);
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error saving variants', severity: 'error' }));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Variants — {product?.name}</DialogTitle>
      <DialogContent>
        {existing.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Existing Variants</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Attribute</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Stock</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {existing.map(v => (
                  <TableRow key={v.id}>
                    <TableCell>{v.attribute_name}</TableCell>
                    <TableCell>{v.attribute_value}</TableCell>
                    <TableCell>{v.sku}</TableCell>
                    <TableCell align="right">₨ {v.price}</TableCell>
                    <TableCell align="right">{v.stock}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}
        <Typography variant="subtitle2" fontWeight={600} mb={1}>Add New Variants</Typography>
        {variants.map((v, i) => (
          <Grid container spacing={1} key={i} sx={{ mb: 1, alignItems: 'center' }}>
            <Grid item xs={2.5}>
              <TextField fullWidth size="small" label="Attribute" value={v.attribute_name} onChange={e => update(i, 'attribute_name', e.target.value)} placeholder="Size / Color" />
            </Grid>
            <Grid item xs={2.5}>
              <TextField fullWidth size="small" label="Value" value={v.attribute_value} onChange={e => update(i, 'attribute_value', e.target.value)} placeholder="XL / Red" />
            </Grid>
            <Grid item xs={2.5}>
              <TextField fullWidth size="small" label="SKU" value={v.sku} onChange={e => update(i, 'sku', e.target.value)} />
            </Grid>
            <Grid item xs={2}>
              <TextField fullWidth size="small" label="Price (₨)" type="number" value={v.price} onChange={e => update(i, 'price', e.target.value)} />
            </Grid>
            <Grid item xs={1.5}>
              <TextField fullWidth size="small" label="Stock" type="number" value={v.stock} onChange={e => update(i, 'stock', e.target.value)} />
            </Grid>
            <Grid item xs={0.5}>
              {variants.length > 1 && <IconButton size="small" color="error" onClick={() => removeRow(i)}><Delete fontSize="small" /></IconButton>}
            </Grid>
          </Grid>
        ))}
        <Button size="small" startIcon={<Add />} onClick={addRow}>Add Row</Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={save} disabled={loading}>Save Variants</Button>
      </DialogActions>
    </Dialog>
  );
}

function ImportDialog({ open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/products/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data.data);
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Import failed', severity: 'error' }));
    } finally { setLoading(false); }
  };

  const downloadTemplate = async () => {
    const { data } = await api.get('/products/import/template', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a'); a.href = url; a.download = 'product_import_template.csv'; a.click();
  };

  const handleClose = () => { setFile(null); setResult(null); if (result) onSuccess(); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Import Products</DialogTitle>
      <DialogContent>
        {!result ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info" action={<Button size="small" startIcon={<Download />} onClick={downloadTemplate}>Template</Button>}>
              Download the CSV template, fill in your products, then upload.
            </Alert>
            <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer' }}
              onClick={() => fileRef.current?.click()}>
              <Upload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography>{file ? file.name : 'Click to select CSV file'}</Typography>
              <input ref={fileRef} type="file" accept=".csv" hidden onChange={e => setFile(e.target.files[0])} />
            </Box>
            {loading && <LinearProgress />}
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Alert severity={result.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              Import complete — {result.imported} imported, {result.failed} skipped
            </Alert>
            {result.errors?.length > 0 && (
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                {result.errors.map((e, i) => <Typography key={i} variant="caption" display="block" color="error">• {e}</Typography>)}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {!result && <Button variant="contained" onClick={handleImport} disabled={!file || loading} startIcon={<Upload />}>Import</Button>}
      </DialogActions>
    </Dialog>
  );
}

export default function ProductsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [variantsProduct, setVariantsProduct] = useState(null);
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ mode: 'percent', adjustment: '', direction: 'increase', field: 'sale_price' });

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/products', { params: { page, limit: 15, search } });
      setProducts(data.data); setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/categories').then(r => setCategories(r.data.data)); }, []);

  const openDialog = (product = null) => {
    setEditingProduct(product);
    reset(product || {});
    setDialogOpen(true);
  };

  const onSubmit = async (data) => {
    try {
      if (editingProduct) await api.put(`/products/${editingProduct.id}`, data);
      else await api.post('/products', data);
      dispatch(showSnackbar({ message: editingProduct ? 'Product updated' : 'Product created', severity: 'success' }));
      setDialogOpen(false); load();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/products/${id}`);
    dispatch(showSnackbar({ message: 'Product deleted', severity: 'success' }));
    load();
  };

  const doBulkPrice = async () => {
    if (!bulkForm.adjustment || +bulkForm.adjustment <= 0) return;
    if (!window.confirm(`This will ${bulkForm.direction} ${bulkForm.field === 'both' ? 'sale & purchase prices' : bulkForm.field} of ALL active products by ${bulkForm.adjustment}${bulkForm.mode === 'percent' ? '%' : ' ₨'}. Continue?`)) return;
    try {
      const { data } = await api.post('/products/bulk-price', {
        mode: bulkForm.mode,
        adjustment: +bulkForm.adjustment,
        direction: bulkForm.direction,
        field: bulkForm.field,
      });
      dispatch(showSnackbar({ message: `${data.data.affected} products updated`, severity: 'success' }));
      setBulkPriceOpen(false);
      setBulkForm({ mode: 'percent', adjustment: '', direction: 'increase', field: 'sale_price' });
      load();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{t('products.products')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<PriceChange />} onClick={() => setBulkPriceOpen(true)} color="warning">Bulk Price</Button>
          <Button variant="outlined" startIcon={<Upload />} onClick={() => setImportOpen(true)}>Import CSV</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>{t('products.addProduct')}</Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <TextField
            placeholder={t('common.search')} size="small" sx={{ mb: 2, width: 300 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />

          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('products.product')}</TableCell>
                  <TableCell>{t('products.sku')}</TableCell>
                  <TableCell>{t('products.category')}</TableCell>
                  <TableCell align="right">{t('products.salePrice')}</TableCell>
                  <TableCell align="right">{t('products.stock')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Clickable image — click to upload */}
                        <Box
                          component="label"
                          sx={{
                            width: 44, height: 44, borderRadius: 1.5, overflow: 'hidden', flexShrink: 0,
                            cursor: 'pointer', position: 'relative',
                            border: '2px dashed', borderColor: p.image_url ? 'transparent' : 'divider',
                            bgcolor: p.image_url ? 'transparent' : 'action.hover',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            '&:hover .img-overlay': { opacity: 1 },
                          }}
                        >
                          {p.image_url
                            ? <Box component="img"
                                src={`${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api','')}${p.image_url}`}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={e => { e.target.style.display='none'; }}
                              />
                            : <PhotoCamera sx={{ fontSize: 20, color: 'text.disabled' }} />
                          }
                          {/* Hover overlay */}
                          <Box className="img-overlay" sx={{
                            position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: 0, transition: 'opacity .2s',
                          }}>
                            {uploadingId === p.id
                              ? <CircularProgress size={16} sx={{ color: 'white' }} />
                              : <PhotoCamera sx={{ fontSize: 18, color: 'white' }} />
                            }
                          </Box>
                          <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                            onChange={async (e) => {
                              if (!e.target.files[0]) return;
                              setUploadingId(p.id);
                              const fd = new FormData(); fd.append('image', e.target.files[0]);
                              try {
                                await api.post(`/products/${p.id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                dispatch(showSnackbar({ message: 'Image uploaded successfully', severity: 'success' }));
                                load();
                              } catch {
                                dispatch(showSnackbar({ message: 'Upload failed — max 5MB, JPG/PNG/WebP only', severity: 'error' }));
                              } finally { setUploadingId(null); e.target.value = ''; }
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                          {p.barcode && <Typography variant="caption" color="text.secondary">{p.barcode}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{p.sku}</TableCell>
                    <TableCell>{p.category_name}</TableCell>
                    <TableCell align="right">₨ {p.sale_price}</TableCell>
                    <TableCell align="right">
                      <Chip label={p.stock} size="small" color={p.stock <= p.low_stock_alert ? 'warning' : 'success'} />
                    </TableCell>
                    <TableCell><Chip label={p.is_active ? t('common.active') : t('common.inactive')} size="small" color={p.is_active ? 'success' : 'default'} /></TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openDialog(p)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="secondary" title="Variants" onClick={() => setVariantsProduct(p)}><Tune fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteProduct(p.id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load} />
      <VariantsDialog product={variantsProduct} open={!!variantsProduct} onClose={() => setVariantsProduct(null)} />

      {/* Product dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingProduct ? 'Edit Product' : t('products.addProduct')}</DialogTitle>
        <DialogContent>
          <Box component="form" id="product-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={8}><TextField fullWidth label={t('products.product')} {...register('name', { required: true })} error={!!errors.name} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth select label={t('products.category')} defaultValue="" {...register('category_id')}>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField></Grid>
              <Grid item xs={6} md={3}><TextField fullWidth label={t('products.sku')} {...register('sku')} /></Grid>
              <Grid item xs={6} md={3}><TextField fullWidth label={t('products.barcode')} {...register('barcode')} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label={t('products.purchasePrice')} type="number" {...register('purchase_price')} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label={t('products.salePrice')} type="number" {...register('sale_price', { required: true })} error={!!errors.sale_price} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label={t('products.wholesalePrice')} type="number" {...register('wholesale_price')} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label={t('products.lowStockAlert')} type="number" defaultValue={5} {...register('low_stock_alert')} /></Grid>
              <Grid item xs={12}><TextField fullWidth label={t('common.notes')} multiline rows={2} {...register('description')} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="product-form">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Price Update dialog */}
      <Dialog open={bulkPriceOpen} onClose={() => setBulkPriceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PriceChange color="warning" /> Bulk Price Update
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField select label="Price Field" value={bulkForm.field} onChange={e => setBulkForm(f => ({ ...f, field: e.target.value }))} size="small" fullWidth>
            <MenuItem value="sale_price">Sale Price only</MenuItem>
            <MenuItem value="purchase_price">Purchase Price only</MenuItem>
            <MenuItem value="both">Both (Sale + Purchase)</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField select label="Direction" value={bulkForm.direction} onChange={e => setBulkForm(f => ({ ...f, direction: e.target.value }))} size="small" sx={{ flex: 1 }}>
              <MenuItem value="increase">Increase ↑</MenuItem>
              <MenuItem value="decrease">Decrease ↓</MenuItem>
            </TextField>
            <TextField select label="Mode" value={bulkForm.mode} onChange={e => setBulkForm(f => ({ ...f, mode: e.target.value }))} size="small" sx={{ flex: 1 }}>
              <MenuItem value="percent">By %</MenuItem>
              <MenuItem value="fixed">By ₨ (fixed)</MenuItem>
            </TextField>
          </Box>
          <TextField
            label={bulkForm.mode === 'percent' ? 'Percentage (%)' : 'Amount (₨)'}
            type="number" value={bulkForm.adjustment} size="small" fullWidth
            onChange={e => setBulkForm(f => ({ ...f, adjustment: e.target.value }))}
            inputProps={{ min: 0.01, step: bulkForm.mode === 'percent' ? 0.5 : 1 }}
          />
          <Alert severity="warning" sx={{ fontSize: 13 }}>
            This will update <strong>all active products</strong>. This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPriceOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={doBulkPrice} disabled={!bulkForm.adjustment}>
            Apply to All Products
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
